/**
 * TanStack Query Hook for Active Scaleups KPI
 *
 * This hook provides access to the active scaleups KPI data from the database.
 * It uses TanStack Query for efficient data fetching with automatic caching,
 * background refetching, and error handling.
 *
 * Database Source: web.v_kpi_scaleups_active_total_v1 view
 *
 * @module useKpiScaleupsActiveTotalV1
 * @category Database Hooks
 *
 * @example
 * ```typescript
 * // Basic usage in a component
 * import { useKpiScaleupsActiveTotalV1 } from '@/hooks/database/useKpiScaleupsActiveTotalV1';
 *
 * function ActiveScaleupsDisplay() {
 *   const { data, isLoading, error } = useKpiScaleupsActiveTotalV1();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return (
 *     <div>
 *       <h2>Active Scaleups</h2>
 *       <p>Count: {data.scaleup_count}</p>
 *       <p>Last Updated: {new Date(data.calculation_date).toLocaleDateString()}</p>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Advanced usage with refetch control
 * function KpiDashboard() {
 *   const { data, isLoading, error, refetch, isFetching } = useKpiScaleupsActiveTotalV1();
 *
 *   const handleRefresh = () => {
 *     refetch();
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleRefresh} disabled={isFetching}>
 *         {isFetching ? 'Refreshing...' : 'Refresh Data'}
 *       </button>
 *       {data && <KpiCard kpi={data} />}
 *     </div>
 *   );
 * }
 * ```
 */

import { useQuery, UseQueryResult, QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { KpiScaleupsActiveTotal } from '@/types/kpi.types';

/**
 * Fetches active scaleups KPI data from the database
 *
 * This function queries the web.v_kpi_scaleups_active_total_v1 view and returns
 * a single row with the current active scaleups count.
 *
 * @returns {Promise<KpiScaleupsActiveTotal>} KPI data object with scaleup count and metadata
 * @throws {Error} If the database query fails or returns invalid data
 *
 * @internal
 */
const fetchKpiScaleupsActiveTotal = async (): Promise<KpiScaleupsActiveTotal> => {
  const { data, error } = await supabase
    .schema('web')
    .from('v_kpi_scaleups_active_total_v1')
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to fetch active scaleups KPI: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from active scaleups KPI query');
  }

  return data;
};

/**
 * React Hook for Active Scaleups KPI Data
 *
 * Fetches and caches the active scaleups KPI data using TanStack Query.
 * The data is cached for 1 hour and will not automatically refetch on
 * window focus or component mount.
 *
 * Query Configuration:
 * - Query Key: ['kpi', 'scaleups-active-total', 'v1']
 * - Stale Time: 1 hour (3600000ms)
 * - Refetch on Window Focus: false
 * - Refetch on Mount: false
 * - Retry Attempts: 2
 *
 * @returns {UseQueryResult<KpiScaleupsActiveTotal>} TanStack Query result object
 * @property {KpiScaleupsActiveTotal | undefined} data - The KPI data (undefined during loading)
 * @property {boolean} isLoading - True if query is in initial loading state
 * @property {boolean} isFetching - True if query is fetching (including background fetches)
 * @property {boolean} isError - True if query encountered an error
 * @property {Error | null} error - The error object if query failed
 * @property {Function} refetch - Function to manually refetch the data
 *
 * @example
 * ```typescript
 * // Simple usage
 * const { data, isLoading } = useKpiScaleupsActiveTotalV1();
 * console.log(data?.scaleup_count); // e.g., 42
 * ```
 *
 * @example
 * ```typescript
 * // Error handling
 * const { data, isLoading, isError, error } = useKpiScaleupsActiveTotalV1();
 *
 * if (isLoading) {
 *   return <Spinner />;
 * }
 *
 * if (isError) {
 *   return <ErrorAlert message={error?.message} />;
 * }
 *
 * return <KpiDisplay value={data.scaleup_count} />;
 * ```
 *
 * @example
 * ```typescript
 * // Manual refetch
 * const { data, refetch } = useKpiScaleupsActiveTotalV1();
 *
 * const handleUpdate = async () => {
 *   await refetch();
 *   toast.success('KPI data refreshed');
 * };
 * ```
 */
export const useKpiScaleupsActiveTotalV1 = (): UseQueryResult<KpiScaleupsActiveTotal> => {
  return useQuery<KpiScaleupsActiveTotal>({
    queryKey: ['kpi', 'scaleups-active-total', 'v1'],
    queryFn: fetchKpiScaleupsActiveTotal,
    staleTime: 3600000, // 1 hour in milliseconds
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });
};

/**
 * Query Key Factory for Active Scaleups KPI
 *
 * Provides consistent query key generation for TanStack Query.
 * Useful for manual cache manipulation or query invalidation.
 *
 * @returns {string[]} The query key array
 *
 * @example
 * ```typescript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { getKpiScaleupsActiveTotalQueryKey } from '@/hooks/database/useKpiScaleupsActiveTotalV1';
 *
 * function RefreshButton() {
 *   const queryClient = useQueryClient();
 *
 *   const invalidateCache = () => {
 *     queryClient.invalidateQueries({
 *       queryKey: getKpiScaleupsActiveTotalQueryKey()
 *     });
 *   };
 *
 *   return <button onClick={invalidateCache}>Clear Cache</button>;
 * }
 * ```
 */
export const getKpiScaleupsActiveTotalQueryKey = (): string[] => {
  return ['kpi', 'scaleups-active-total', 'v1'];
};

/**
 * Prefetch Function for Active Scaleups KPI
 *
 * Allows prefetching the KPI data before component mount.
 * Useful for optimistic data loading and improving perceived performance.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient - TanStack Query client instance
 * @returns {Promise<void>}
 *
 * @example
 * ```typescript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { prefetchKpiScaleupsActiveTotal } from '@/hooks/database/useKpiScaleupsActiveTotalV1';
 *
 * function App() {
 *   const queryClient = useQueryClient();
 *
 *   useEffect(() => {
 *     // Prefetch KPI data on app load
 *     prefetchKpiScaleupsActiveTotal(queryClient);
 *   }, [queryClient]);
 *
 *   return <Dashboard />;
 * }
 * ```
 */
export const prefetchKpiScaleupsActiveTotal = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.prefetchQuery({
    queryKey: getKpiScaleupsActiveTotalQueryKey(),
    queryFn: fetchKpiScaleupsActiveTotal,
    staleTime: 3600000,
  });
};
